import React, { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { FileEdit } from "lucide-react";
import { ThemeProvider } from "./components/ThemeContext.tsx";
import { ChatProvider } from "./context/ChatContext";
import { AuthProvider } from "./context/AuthContext.tsx";
// import { ChatAssistant } from "./components/chat"; // Removed chat assistant

// ScrollToTop component to scroll to top on every route change
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    // Immediately scroll to top without animation
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'instant'
    });

    // Also set scroll position directly as backup
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;

    // Handle any delayed content loading with a small timeout
    const timeoutId = setTimeout(() => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [pathname]);

  return null;
}

// Layout Components
// import Header from "./components/layout/Header"; // Component not available
import PrivateHeader from "./components/PrivateHeader.tsx"; // Fixed import path
import PublicHeader from "./components/PublicHeader.jsx"; // Added for /old route
import Sidebar from "./components/Sidebar.tsx"; // New import
import ProtectedRoute from "./components/ProtectedRoute.tsx"; // Protected route wrapper
import RouteErrorBoundary from "./components/RouteErrorBoundary";
import NotFoundPage from "./pages/NotFoundPage";
import PageTour from "./components/tour/PageTour";
import ActivityLogSidebar from "./components/ActivityLogSidebar.tsx";
import ActivityTracker from "./components/ActivityTracker.tsx";
import useScreenshotProtection from "./hooks/useScreenshotProtection";

// Auth Pages
import UniversalLoginPage from "./pages/auth/UniversalLoginPage.jsx";

// DEV-ONLY: chart stress harness. Both conditions are compile-time constants, so in a normal
// production build the lazy import (and its chunk) is removed entirely. Set REACT_APP_CHART_LAB=1
// at build time only to benchmark a production bundle locally.
const ChartStressLab = process.env.NODE_ENV === "development" || process.env.REACT_APP_CHART_LAB === "1"
  ? React.lazy(() => import("./pages/dev/ChartStressLab"))
  : null;
import {
  SiteSelection,
  SuperAdminCompanySelection,
  SignupPage,
  UserProfilePage,
  ForgotPasswordPage,
  OTPVerificationPage
} from "./pages/auth";
import ResetPasswordPage from "./pages/auth/ResetPasswordPage.jsx";
// Platform guide is code-split: it is only needed when someone opens it.
const PlatformGuidePage = React.lazy(() => import("./pages/guide/PlatformGuidePage"));
import UserProfileEditPage from "./pages/UserProfileEditPage";
import DashboardWrapper from "./components/DashboardWrapper";
import DashboardLayout from "./components/DashboardLayout";

// Public Pages
import {
  LandingPage,
  AboutPage,
  ServicesPage,
  ProjectsPage,
  TeamPage,
  ContactPage,
  FAQPage,
  NewsPage,
  NewsDetailsPage
} from "./pages/public";
import NewHomePage from "./pages/public/NewHomePage";

// Dashboard Pages (from src/)
import Dashboard from "./pages/dashboard-pages/Dashboard/DashboardPage.tsx";
import HistogramChartPage from "./pages/dashboard-pages/Dashboard/HistogramChartPage.tsx";
import NavigationPage from "./pages/dashboard-pages/NavigationPage.tsx";
import AxonPage from "./pages/dashboard-pages/AxonPage.tsx";

// DGR Pages (from src/)
import DGRUploadPage from "./pages/dashboard-pages/dgr_upload/DGRUploadPage.tsx";

// Site Info Pages
import { ACSEPL } from "./pages/site-info";
import AXON from "./site-info/AXON.jsx";
import SiteInfo from "./pages/dashboard-pages/SiteInfo.tsx";

// New Dashboard Pages (from src/)
import AlertsPortal from "./pages/dashboard-pages/alerts/AlertsPortal.tsx";
import SimulationOnDemand from "./pages/dashboard-pages/simulation/SimulationOnDemand.tsx";
import InventoryPage from "./pages/dashboard-pages/inventory/InventoryPage.tsx";
import VendorPage from "./pages/vendors/VendorPage.jsx";
import ProfilePage from "./pages/ProfilePage.tsx";
import DocumentationPage from "./pages/dashboard-pages/documentation/DocumentationPage.tsx";
import OMPage from "./pages/dashboard-pages/om/OMPage.tsx";
import DSMPage from "./pages/dashboard-pages/dsm/DSMPage.tsx";
import PTWPage from "./pages/dashboard-pages/om/PTWPage.tsx";
import CompliancePage from "./pages/dashboard-pages/compliance/CompliancePage.tsx";

// Admin Pages
import AdminDashboard from "./pages/admin/AdminDashboard";
import UserManagement from "./pages/admin/UserManagement";
import UserCreate from "./pages/admin/UserCreate";
import UserEdit from "./pages/admin/UserEdit";
import SiteManagement from "./pages/admin/SiteManagement";
import CompanyManagement from "./pages/admin/CompanyManagement";
import TokenManagement from "./pages/admin/TokenManagement";
import CompanyDetail from "./pages/admin/CompanyDetail";
import SiteEdit from "./pages/admin/SiteEdit";
import SiteCreate from "./pages/admin/SiteCreate";
import SiteDetail from "./pages/admin/SiteDetail";
import SecurityMonitoring from "./pages/admin/SecurityMonitoring";
import UserActivityPage from "./pages/admin/UserActivityPage";
import SiteAttendancePage from "./pages/admin/SiteAttendancePage";
import DataCleanupManagement from "./pages/admin/DataCleanupManagement";
import DataDeletionRequests from "./pages/admin/DataDeletionRequests";
const ChartManagementPage = React.lazy(() => import("./pages/admin/chart-management/ChartManagementPage"));

import AdminRoute from "./components/AdminRoute";
import AdminLayout from "./components/admin/AdminLayout";

// Additional UI Pages
import LandingPageUI from "./pages/landingpage.tsx";

// Service Pages
import AssetPerformancePage from "./pages/services/AssetPerformancePage";
import AIAnalyticsPage from "./pages/services/AIAnalyticsPage";
// import DroneThermographyPage from "./pages/services/DroneThermographyPage"; // Commented out for future use
import PortfolioManagementPage from "./pages/services/PortfolioManagementPage";

// New Service Pages
import DataIngestionPage from './pages/services/DataIngestionPage';
import WindTurbineMonitoringPage from './pages/services/WindTurbineMonitoringPage';
import WindTurbinesAIPage from './pages/services/WindTurbinesAIPage';
import SolarAdvancedAnalyticsPage from './pages/services/SolarAdvancedAnalyticsPage';
import WindEnergyRepoweringPage from './pages/services/WindEnergyRepoweringPage';
import CleanEnergyDataAnalysisPage from "./pages/services/CleanEnergyDataAnalysisPage";
import RenewableTechnologyMonitoringPage from "./pages/services/RenewableTechnologyMonitoringPage";
import EnergyInnovationAIPage from "./pages/services/EnergyInnovationAIPage";
import SolarEnergyFarmPage from "./pages/services/SolarEnergyFarmPage";
import AssetPerformanceMonitoringPage from "./pages/services/AssetPerformanceMonitoringPage";

// Additional Service Pages
import AssetMonitoringPage from "./pages/services/AssetMonitoringPage";
import PerformanceAnalyticsPage from "./pages/services/PerformanceAnalyticsPage";
import RTCPowerOptimizationPage from "./pages/services/RTCPowerOptimizationPage";
import EnergyTradingOptimizationPage from "./pages/services/EnergyTradingOptimizationPage";
import ImageVideoAnalyticsPage from "./pages/services/ImageVideoAnalyticsPage";
import OfflineMeasurementPage from "./pages/services/OfflineMeasurementPage";
import SparesVendorsManagementPage from "./pages/services/SparesVendorsManagementPage";

// New Pages
import PricingPage from "./pages/pricing";
import CaseStudiesPage from "./pages/case-studies";
import BlogPage from "./pages/blog";

// Legal & Store Compliance Pages
import {
  PrivacyPolicyPage,
  TermsAndConditionsPage,
  AccountDeletionPage
} from "./pages/legal";

// Aceternity UI Demo - Component not available
// import AceternityDemo from "./components/aceternity/AceternityDemo";

function App() {
  // Clear only specific cached credentials (not the token - that's managed by AuthContext)
  useEffect(() => {
    // Clear old remembered credentials but keep token (let AuthContext handle token validation)
    localStorage.removeItem('rememberedSite');
    localStorage.removeItem('rememberedUsername');
    localStorage.removeItem('rememberedPassword');
    localStorage.removeItem('users');
    // Don't clear token here - let AuthContext handle token validation and cleanup
  }, []);

  // Enable global screenshot and content protection
  // useScreenshotProtection();

  return (
    <ThemeProvider>
      <AuthProvider>
        <ChatProvider>
          <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <ScrollToTop />
            <Routes>
              {ChartStressLab && (
                <Route
                  path="/dev/chart-lab"
                  element={<React.Suspense fallback={null}><ChartStressLab /></React.Suspense>}
                />
              )}
              {/* Public Routes */}
              <Route
                path="/"
                element={<NewHomePage />}
              />
              <Route
                path="/about"
                element={<AboutPage />}
              />
              <Route
                path="/service/asset-performance"
                element={<AssetPerformancePage />}
              />
              <Route
                path="/service/ai-analytics"
                element={<AIAnalyticsPage />}
              />
              {/* <Route
            path="/service/drone-thermography"
            element={<DroneThermographyPage />}
          /> */} {/* Commented out for future use */}
              <Route
                path="/service/portfolio-management"
                element={<PortfolioManagementPage />}
              />

              {/* New Service Pages */}
              <Route
                path="/service/data-ingestion"
                element={<DataIngestionPage />}
              />
              <Route
                path="/service/wind-turbine-monitoring"
                element={<WindTurbineMonitoringPage />}
              />
              <Route
                path="/service/wind-turbines-ai"
                element={<WindTurbinesAIPage />}
              />
              <Route
                path="/service/solar-advanced-analytics"
                element={<SolarAdvancedAnalyticsPage />}
              />
              <Route
                path="/service/wind-repowering"
                element={<WindEnergyRepoweringPage />}
              />
              <Route
                path="/service/clean-energy-data-analysis"
                element={<CleanEnergyDataAnalysisPage />}
              />
              <Route
                path="/service/renewable-technology-monitoring"
                element={<RenewableTechnologyMonitoringPage />}
              />
              <Route
                path="/service/energy-innovation-ai"
                element={<EnergyInnovationAIPage />}
              />
              <Route
                path="/service/solar-energy-farm"
                element={<SolarEnergyFarmPage />}
              />
              <Route
                path="/service/asset-performance-monitoring"
                element={<AssetPerformanceMonitoringPage />}
              />

              {/* Additional Service Pages */}
              <Route
                path="/service/asset-monitoring"
                element={<AssetMonitoringPage />}
              />
              <Route
                path="/service/performance-analytics"
                element={<PerformanceAnalyticsPage />}
              />
              <Route
                path="/service/rtc-power-optimization"
                element={<RTCPowerOptimizationPage />}
              />
              <Route
                path="/service/energy-trading-optimization"
                element={<EnergyTradingOptimizationPage />}
              />
              <Route
                path="/service/image-video-analytics"
                element={<ImageVideoAnalyticsPage />}
              />
              <Route
                path="/service/offline-measurement"
                element={<OfflineMeasurementPage />}
              />
              <Route
                path="/service/spares-vendors-management"
                element={<SparesVendorsManagementPage />}
              />

              <Route
                path="/service"
                element={<ServicesPage />}
              />
              <Route
                path="/project"
                element={<ProjectsPage />}
              />
              <Route
                path="/team"
                element={<TeamPage />}
              />
              <Route
                path="/contact"
                element={<ContactPage />}
              />
              <Route
                path="/faq"
                element={<FAQPage />}
              />
              <Route
                path="/news"
                element={<NewsPage />}
              />
              <Route
                path="/news-details/:id"
                element={<NewsDetailsPage />}
              />
              <Route
                path="/pricing"
                element={<PricingPage />}
              />
              <Route
                path="/case-studies"
                element={<CaseStudiesPage />}
              />
              <Route
                path="/blog"
                element={<BlogPage />}
              />

              {/* Legal & Store Compliance Public Routes */}
              <Route
                path="/privacy-policy"
                element={<PrivacyPolicyPage />}
              />
              <Route
                path="/terms-and-conditions"
                element={<TermsAndConditionsPage />}
              />
              <Route
                path="/terms-of-service"
                element={<TermsAndConditionsPage />}
              />
              <Route
                path="/account-deletion"
                element={<AccountDeletionPage />}
              />
              <Route
                path="/data-deletion"
                element={<AccountDeletionPage />}
              />
              <Route
                path="/old"
                element={
                  <>
                    <PublicHeader />
                    <LandingPage />
                    <SiteSelection />
                  </>
                }
              />
              <Route
                path="/select-company"
                element={
                  <ProtectedRoute>
                    <SuperAdminCompanySelection />
                    <PageTour id="selectCompany" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/select-site"
                element={
                  <ProtectedRoute>
                    <SiteSelection />
                    <PageTour id="selectSite" />
                  </ProtectedRoute>
                }
              />

              {/* New User-Friendly Authentication Routes */}
              <Route
                path="/signup"
                element={<SignupPage />}
              />
              <Route
                path="/verify-otp"
                element={<OTPVerificationPage />}
              />
              <Route
                path="/forgot-password"
                element={<ForgotPasswordPage />}
              />
              <Route
                path="/reset-password"
                element={<ResetPasswordPage />}
              />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <UserProfileEditPage />
                  </ProtectedRoute>
                }
              />


              {/* Existing Authentication Routes - Preserved */}
              <Route
                path="/login"
                element={<UniversalLoginPage />}
              />
              <Route
                path="/login/:site"
                element={<UniversalLoginPage />}
              />
              {/* <Route
                path="/forgot-password-dashboard"
                element={<ForgotPasswordPageDashboard />}
              /> */}

              {/* Private Routes - Protected with authentication */}
              <Route
                path="/navigate/:site"
                element={
                  <ProtectedRoute>
                    <PrivateHeader />
                    <DashboardLayout>
                      <RouteErrorBoundary>
                        <NavigationPage />
                      </RouteErrorBoundary>
                      <PageTour id="hub" />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />

              {/* DGR Data Entry Route - Updated to use AxonPage from src/ */}
              <Route
                path="/entry/:site"
                element={
                  <ProtectedRoute>
                    <PrivateHeader />
                    <DashboardLayout>
                      <RouteErrorBoundary>
                        <AxonPage />
                      </RouteErrorBoundary>
                      <PageTour id="entry" />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/upload/:site"
                element={
                  <ProtectedRoute>
                    <PrivateHeader />
                    <DashboardLayout>
                      <RouteErrorBoundary>
                        <DGRUploadPage />
                      </RouteErrorBoundary>
                      <PageTour id="upload" />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />

              {/* Dashboard Route */}
              <Route
                path="/dashboard/:site"
                element={
                  <ProtectedRoute>
                    <PrivateHeader />
                    <DashboardLayout>
                      <Dashboard />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />

              {/* Dashboard Histogram Chart Route */}
              <Route
                path="/dashboard/:site/histogram"
                element={
                  <ProtectedRoute>
                    <PrivateHeader />
                    <DashboardLayout>
                      <RouteErrorBoundary>
                        <HistogramChartPage />
                      </RouteErrorBoundary>
                      <PageTour id="histogram" />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />

              {/* Site Info Pages (from src/) */}
              <Route
                path="/site-info/:site"
                element={
                  <ProtectedRoute>
                    <PrivateHeader />
                    <DashboardLayout>
                      <RouteErrorBoundary>
                        <SiteInfo />
                      </RouteErrorBoundary>
                      <PageTour id="siteinfo" />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />

              {/* New Dashboard Pages */}
              <Route
                path="/alerts/:site"
                element={
                  <ProtectedRoute>
                    <PrivateHeader />
                    <DashboardLayout>
                      <RouteErrorBoundary>
                        <AlertsPortal />
                      </RouteErrorBoundary>
                      <PageTour id="alerts" />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/simulation/:site"
                element={
                  <ProtectedRoute>
                    <PrivateHeader />
                    <DashboardLayout>
                      <RouteErrorBoundary>
                        <SimulationOnDemand />
                      </RouteErrorBoundary>
                      <PageTour id="simulation" />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dsm/:site"
                element={
                  <ProtectedRoute>
                    <PrivateHeader />
                    <DashboardLayout>
                      <RouteErrorBoundary>
                        <DSMPage />
                      </RouteErrorBoundary>
                      <PageTour id="dsm" />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/inventory/:site"
                element={
                  <ProtectedRoute>
                    <PrivateHeader />
                    <DashboardLayout>
                      <RouteErrorBoundary>
                        <InventoryPage />
                      </RouteErrorBoundary>
                      <PageTour id="inventory" />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/vendors/:site"
                element={
                  <ProtectedRoute>
                    <PrivateHeader />
                    <DashboardLayout>
                      <RouteErrorBoundary>
                        <VendorPage />
                      </RouteErrorBoundary>
                      <PageTour id="vendors" />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/documentation/:site"
                element={
                  <ProtectedRoute>
                    <PrivateHeader />
                    <DashboardLayout>
                      <RouteErrorBoundary>
                        <DocumentationPage />
                      </RouteErrorBoundary>
                      <PageTour id="documentation" />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/om/:site"
                element={
                  <ProtectedRoute>
                    <PrivateHeader />
                    <DashboardLayout>
                      <RouteErrorBoundary>
                        <OMPage />
                      </RouteErrorBoundary>
                      <PageTour id="om" />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ptw/:site"
                element={
                  <ProtectedRoute>
                    <PrivateHeader />
                    <DashboardLayout>
                      <RouteErrorBoundary>
                        <PTWPage />
                      </RouteErrorBoundary>
                      <PageTour id="ptw" />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/compliance/:site"
                element={
                  <ProtectedRoute>
                    <PrivateHeader />
                    <DashboardLayout>
                      <RouteErrorBoundary>
                        <CompliancePage />
                      </RouteErrorBoundary>
                      <PageTour id="compliance" />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/guide/:site"
                element={
                  <ProtectedRoute>
                    <PrivateHeader />
                    <DashboardLayout>
                      <React.Suspense fallback={null}>
                        <PlatformGuidePage />
                      </React.Suspense>
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/guide"
                element={
                  <ProtectedRoute>
                    <PrivateHeader />
                    <React.Suspense fallback={null}>
                      <PlatformGuidePage />
                    </React.Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile/:site"
                element={
                  <ProtectedRoute>
                    <PrivateHeader />
                    <DashboardLayout>
                      <RouteErrorBoundary>
                        <ProfilePage />
                      </RouteErrorBoundary>
                      <PageTour id="profile" />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />

              {/* Admin Routes - Only accessible to admin users */}
              <Route
                path="/admin"
                element={
                  <AdminRoute>
                    <PrivateHeader />
                    <AdminLayout>
                      <RouteErrorBoundary>
                        <AdminDashboard />
                      </RouteErrorBoundary>
                      <PageTour id="adminDashboard" />
                    </AdminLayout>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <AdminRoute>
                    <PrivateHeader />
                    <AdminLayout>
                      <RouteErrorBoundary>
                        <UserManagement />
                      </RouteErrorBoundary>
                      <PageTour id="adminUsers" />
                    </AdminLayout>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/users/create"
                element={
                  <AdminRoute>
                    <PrivateHeader />
                    <AdminLayout>
                      <RouteErrorBoundary>
                        <UserCreate />
                      </RouteErrorBoundary>
                    </AdminLayout>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/users/:id"
                element={
                  <AdminRoute>
                    <PrivateHeader />
                    <AdminLayout>
                      <RouteErrorBoundary>
                        <UserEdit />
                      </RouteErrorBoundary>
                    </AdminLayout>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/users/:id/edit"
                element={
                  <AdminRoute>
                    <PrivateHeader />
                    <AdminLayout>
                      <RouteErrorBoundary>
                        <UserEdit />
                      </RouteErrorBoundary>
                    </AdminLayout>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/sites"
                element={
                  <AdminRoute>
                    <PrivateHeader />
                    <AdminLayout>
                      <RouteErrorBoundary>
                        <SiteManagement />
                      </RouteErrorBoundary>
                      <PageTour id="adminSites" />
                    </AdminLayout>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/companies"
                element={
                  <AdminRoute>
                    <PrivateHeader />
                    <AdminLayout>
                      <RouteErrorBoundary>
                        <CompanyManagement />
                      </RouteErrorBoundary>
                      <PageTour id="adminCompanies" />
                    </AdminLayout>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/companies/:id"
                element={
                  <AdminRoute>
                    <PrivateHeader />
                    <AdminLayout>
                      <RouteErrorBoundary>
                        <CompanyDetail />
                      </RouteErrorBoundary>
                    </AdminLayout>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/tokens"
                element={
                  <AdminRoute>
                    <PrivateHeader />
                    <AdminLayout>
                      <RouteErrorBoundary>
                        <TokenManagement />
                      </RouteErrorBoundary>
                      <PageTour id="adminTokens" />
                    </AdminLayout>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/sites/create"
                element={
                  <AdminRoute>
                    <PrivateHeader />
                    <AdminLayout>
                      <RouteErrorBoundary>
                        <SiteCreate />
                      </RouteErrorBoundary>
                    </AdminLayout>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/sites/:id"
                element={
                  <AdminRoute>
                    <PrivateHeader />
                    <AdminLayout>
                      <RouteErrorBoundary>
                        <SiteDetail />
                      </RouteErrorBoundary>
                    </AdminLayout>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/sites/:id/edit"
                element={
                  <AdminRoute>
                    <PrivateHeader />
                    <AdminLayout>
                      <RouteErrorBoundary>
                        <SiteEdit />
                      </RouteErrorBoundary>
                    </AdminLayout>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/security"
                element={
                  <AdminRoute>
                    <PrivateHeader />
                    <AdminLayout>
                      <RouteErrorBoundary>
                        <SecurityMonitoring />
                      </RouteErrorBoundary>
                      <PageTour id="adminSecurity" />
                    </AdminLayout>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/user-activity"
                element={
                  <AdminRoute>
                    <PrivateHeader />
                    <AdminLayout>
                      <RouteErrorBoundary>
                        <UserActivityPage />
                      </RouteErrorBoundary>
                      <PageTour id="adminActivity" />
                    </AdminLayout>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/site-attendance"
                element={
                  <AdminRoute>
                    <PrivateHeader />
                    <AdminLayout>
                      <RouteErrorBoundary>
                        <SiteAttendancePage />
                      </RouteErrorBoundary>
                      <PageTour id="adminAttendance" />
                    </AdminLayout>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/chart-management"
                element={
                  <AdminRoute>
                    <PrivateHeader />
                    <AdminLayout>
                      <RouteErrorBoundary>
                        <React.Suspense fallback={null}>
                          <ChartManagementPage />
                        </React.Suspense>
                      </RouteErrorBoundary>
                    </AdminLayout>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/data-cleanup"
                element={
                  <AdminRoute>
                    <PrivateHeader />
                    <AdminLayout>
                      <RouteErrorBoundary>
                        <DataCleanupManagement />
                      </RouteErrorBoundary>
                      <PageTour id="adminCleanup" />
                    </AdminLayout>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/data-deletion-requests"
                element={
                  <AdminRoute>
                    <PrivateHeader />
                    <AdminLayout>
                      <RouteErrorBoundary>
                        <DataDeletionRequests />
                      </RouteErrorBoundary>
                      <PageTour id="adminDeletion" />
                    </AdminLayout>
                  </AdminRoute>
                }
              />


              {/* Any unknown URL: a real "not found" page instead of a blank screen */}
              <Route path="*" element={<NotFoundPage />} />

              {/* Aceternity UI Demo Route - Component not available */}
              {/* <Route
            path="/aceternity-demo"
            element={<AceternityDemo />}
          /> */}
            </Routes>
            {/* <ChatAssistant /> */} {/* Removed chat assistant */}
            <ActivityTracker />
          </Router>
        </ChatProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
